import { AsyncQueuer } from "@tanstack/pacer";
import { Store } from "@tanstack/store";
import { pagesCollection, trackPageMutation, settleRow } from "./collections/pages-collection";
import { scriptsCollection } from "./collections/scripts-collection";
import { blobStore } from "./blob-store";
import { pageFileName } from "./drive-naming";
import { patchesForZeroBasedPages } from "./page-index";
import {
  ConnectivityException,
  StoreAuthException,
  StoreConflictException,
  StoreUnreachableException,
} from "./api/errors";
import type { NyntaxHttpStore } from "./api/client";
import type { Page } from "./page";
import { scriptMetadataPayload } from "./script";
import { isUploadReady, type BlobPort } from "./page-persist";

export type PauseReason = "auth" | "unreachable" | "offline";

/** How long an `auth` pause stays in place before the sweep tries again.
 * Long enough for the banner to be read and acted on, short enough that a
 * token fixed on the server drains the backlog without anyone reloading. */
const AUTH_PROBE_MS = 60000;

export interface QueueState {
  paused: boolean;
  pauseReason: PauseReason | null;
}

export function backoffMs(attemptCount: number, random: () => number = Math.random): number {
  const base = 5000 * Math.pow(2, attemptCount - 1);
  const capped = Math.min(base, 300000);
  const jitterFraction = random() * 0.4 - 0.2;
  return Math.round(capped * (1 + jitterFraction));
}

/** Pages sitting at "uploading" that no live task owns. The status is
 * persisted, so a tab closed mid-upload reopens with the page still
 * "uploading" — and `isEligible` only ever takes pending/failed, so nothing
 * would pick it up again: the script would read "Uploading" forever. Any
 * page the caller isn't currently running is therefore an orphan to requeue.
 * (With two tabs open on the same scripts, the idle tab requeues the busy
 * tab's page and both upload it; the server dedupes on name + checksum.) */
export function stuckPageIds(pages: Page[], inFlight: ReadonlySet<string>): string[] {
  return pages.filter(p => p.uploadStatus === "uploading" && !inFlight.has(p.id)).map(p => p.id);
}

/** Pages a manual "Upload now" should pick up. `failed` pages would return
 * on their own once their backoff expires — but that is up to five minutes
 * after the server comes back, which is a long time to stand next to a
 * scanner. `blocked` pages would never return at all. `pending` pages are
 * here too, and they are the reason this isn't just "retry": a global pause
 * (auth, unreachable, offline) hands the claimed page back as `pending`, so
 * during exactly the outage the button exists for, nothing is `failed` and a
 * failed-only trigger would vanish from the screen.
 *
 * Pages whose checksum hasn't landed yet (`sha256` empty) are left out: the
 * queue can't upload them, and flipping an `idb-write`/`checksum` failure to
 * `pending` would only bury its "retake this page" message under a status
 * that never moves. */
export function manualUploadPageIds(pages: Page[]): string[] {
  return pages
    .filter(
      p =>
        isUploadReady(p) &&
        (p.uploadStatus === "pending" || p.uploadStatus === "failed" || p.uploadStatus === "blocked"),
    )
    .map(p => p.id);
}

/** Rows a device-wide force push has to write. `pending` pages are
 * deliberately absent even though they're part of the backlog: they are
 * already eligible, so `resume()` + `runOnce()` picks them up without a
 * write — and on a phone holding a few hundred unsent pages, rewriting
 * every row is the difference between a button that responds and one that
 * locks the screen for several seconds. Only pages the queue is actively
 * holding back — in backoff, or blocked — need touching. */
export function forceUploadPageIds(pages: Page[]): string[] {
  return pages
    .filter(p => isUploadReady(p) && (p.uploadStatus === "failed" || p.uploadStatus === "blocked"))
    .map(p => p.id);
}

function isEligible(page: Page, now: number): boolean {
  if (!isUploadReady(page)) return false;
  return (
    page.uploadStatus === "pending" ||
    (page.uploadStatus === "failed" && (page.nextAttemptAt ?? 0) <= now)
  );
}

export class UploadQueue {
  readonly stateStore = new Store<QueueState>({ paused: false, pauseReason: null });

  private folderResolution = new Map<string, Promise<string>>();

  /** Real concurrency for page uploads. Pacer's own per-task retry is
   * disabled (maxAttempts: 1) — backoff/retry stays hand-rolled below so
   * it survives reloads and stays visible per-page in the UI, matching
   * the mobile app's semantics; see the queue-engine design doc. */
  private queuer = new AsyncQueuer<Page>(page => this.processPage(page), {
    concurrency: 6,
    started: true,
    asyncRetryerOptions: { maxAttempts: 1 },
  });

  /** Ids claimed by this instance and not yet finished — claimed pages wait
   * in the queuer before `processPage` runs, so being in flight is wider
   * than being in `processPage`. Guards the reclaim below. */
  private inFlight = new Set<string>();

  private retryTimer: ReturnType<typeof setInterval> | null = null;

  private onVisible: (() => void) | null = null;

  /** When the current `auth` pause started. See AUTH_PROBE_MS. */
  private authPausedAt = 0;

  /** Backoff-failed pages only become eligible again once `nextAttemptAt`
   * passes — nothing else re-checks them. Without this the queue only
   * drains on capture/upload events, so a page in backoff sits stuck
   * until the scanner happens to trigger another runOnce() (e.g. by
   * returning to Home, which used to own this interval itself). */
  startAutoRetry(intervalMs = 5000): void {
    if (this.retryTimer) return;
    this.retryTimer = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (this.paused) {
        if (!this.readyToProbe()) return;
        this.resume();
      }
      void this.runOnce();
    }, intervalMs);
    // A backgrounded tab is skipped above, and mobile browsers background
    // one the moment the scanner switches apps or the screen locks. Waiting
    // for the next tick after they come back wastes up to `intervalMs` of
    // the few seconds the app may get in the foreground.
    if (typeof document !== "undefined") {
      this.onVisible = () => {
        if (!document.hidden) void this.runOnce();
      };
      document.addEventListener("visibilitychange", this.onVisible);
    }
  }

  stopAutoRetry(): void {
    if (this.retryTimer) clearInterval(this.retryTimer);
    this.retryTimer = null;
    if (this.onVisible && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.onVisible);
    }
    this.onVisible = null;
  }

  /** Whether the sweep may lift the current pause and try again.
   *
   * An offline or unreachable pause clears the moment the network is back,
   * so those are probed every tick. An `auth` pause is different: a rotated
   * or expired token is not something retrying in five seconds can fix, and
   * clearing the pause on every tick — which this used to do
   * unconditionally — erased the "authentication failed" banner within 5s
   * of it appearing. What the scanner then saw was "Uploading in the
   * background…" forever, while every attempt 401'd. Probing occasionally
   * still lets a fixed token recover on its own, without a reload. */
  private readyToProbe(): boolean {
    if (this.pauseReason !== "auth") return true;
    return this.now() - this.authPausedAt >= AUTH_PROBE_MS;
  }

  constructor(
    private store: NyntaxHttpStore,
    private scannerName: string,
    private now: () => number = Date.now,
    private random: () => number = Math.random,
    /** The page images themselves. Injectable so the pipeline test can drive
     * the real queue against a real HTTP server without IndexedDB. */
    private blobs: BlobPort = blobStore,
  ) {}

  get paused(): boolean {
    return this.stateStore.state.paused;
  }

  get pauseReason(): PauseReason | null {
    return this.stateStore.state.pauseReason;
  }

  /** Returns orphaned "uploading" pages to the queue so an interrupted
   * upload resumes instead of stalling. Cheap and idempotent — runs on every
   * drain, not just at launch, so a page orphaned mid-session (or one whose
   * row hydrated after the launch pass) still recovers. */
  private reclaimStuck(): void {
    for (const id of stuckPageIds(pagesCollection.toArray, this.inFlight)) {
      this.updatePage(id, { uploadStatus: "pending" });
    }
  }

  resetStuckOnLaunch(): void {
    this.reclaimStuck();
    for (const patch of patchesForZeroBasedPages(pagesCollection.toArray)) {
      pagesCollection.update(patch.id, draft => {
        draft.pageIndex = patch.pageIndex;
        draft.nextAttemptAt = 0;
      });
    }
  }

  /** Clears backoff, un-blocks, and drains immediately — the scanner's
   * "go now" for pages the queue would otherwise sit on. `failReason` is
   * deliberately kept: handleChecksumMismatch reads it to decide that a
   * second corrupt upload blocks for good rather than looping. Retrying a
   * `remote-mismatch` page will simply re-block, since the same remote
   * file is still there — retake is the fix, and the page says so. */
  async retryNow(pages: Page[]): Promise<number> {
    for (const id of manualUploadPageIds(pages)) {
      this.updatePage(id, { uploadStatus: "pending", nextAttemptAt: 0 });
    }
    this.resume();
    return this.runOnce();
  }

  /** The device-wide "go now": one action that lifts every hold the queue
   * has put on this phone's backlog, for the case `retryNow` doesn't cover
   * — a scanner with pages spread across dozens of scripts, where tapping
   * a per-script button on each is not a plan, and nobody can say whether
   * the queue is slow or stopped.
   *
   * Beyond `retryNow` it also resets `attemptCount`. After an outage every
   * page has climbed to the 5-minute backoff ceiling, so the first attempt
   * after this push is immediate but the *next* one would be five minutes
   * later — long after whoever pressed the button has walked away. Zeroing
   * it puts them back on the 5s-and-doubling ladder. Returns the number of
   * held-back pages it released, which is what the caller reports. */
  async forceUploadAll(pages: Page[]): Promise<number> {
    const ids = forceUploadPageIds(pages);
    for (const id of ids) {
      this.updatePage(id, { uploadStatus: "pending", nextAttemptAt: 0, attemptCount: 0 });
    }
    this.resume();
    await this.runOnce();
    return ids.length;
  }

  /** Claims eligible pages and hands them to the concurrent queuer. Returns
   * immediately once claimed pages are enqueued — uploads themselves run
   * in the background across queuer ticks, not gated on this call. */
  async runOnce(workers = 6): Promise<number> {
    if (this.paused) return 0;
    this.reclaimStuck();
    const now = this.now();
    const eligible = pagesCollection.toArray.filter(p => isEligible(p, now)).slice(0, workers);
    let enqueued = 0;
    for (const page of eligible) {
      const claimed = await this.claim(page.id);
      if (!claimed) continue;
      this.queuer.addItem(claimed);
      enqueued++;
    }
    return enqueued;
  }

  resume(): void {
    this.stateStore.setState(() => ({ paused: false, pauseReason: null }));
  }

  /** Only claims if the page is still pending/failed — mirrors the previous
   * repository's rowsAffected-based guard against a stale eligibility list.
   * Settles on the row before and after its own write — see
   * addPageFromBlob's comment in page-persist.ts for why that matters. */
  private async claim(id: string): Promise<Page | null> {
    await settleRow(id);
    const current = pagesCollection.get(id);
    if (!current) return null;
    if (current.uploadStatus !== "pending" && current.uploadStatus !== "failed") return null;
    const attemptCount = current.attemptCount + 1;
    this.inFlight.add(id);
    trackPageMutation(
      id,
      pagesCollection.update(id, draft => {
        draft.uploadStatus = "uploading";
        draft.attemptCount = attemptCount;
      }),
    );
    return { ...current, uploadStatus: "uploading", attemptCount };
  }

  private async processPage(page: Page): Promise<void> {
    await settleRow(page.id);
    try {
      const script = scriptsCollection.get(page.scriptId);
      // No script row to upload into. Back off and retry rather than
      // returning: leaving the page at "uploading" would strand it, since
      // the status is the only thing that makes it eligible again.
      if (!script) {
        this.scheduleRetry(page, "missing-script");
        return;
      }
      const folderId = await this.resolveFolder(script.id, script.remoteId);
      const name = pageFileName(page.pageIndex);
      const remote = await this.store.findPage(folderId, name);

      if (remote?.sha256 === page.sha256) {
        this.markUploaded(page.id, remote.id);
        return;
      }
      // A different image already sits under this index. That's a conflict
      // worth blocking on — unless this row is a retake, in which case the
      // scanner deliberately re-shot the page and the server copy is the one
      // to overwrite.
      if (remote && !page.replaceRemote) {
        this.updatePage(page.id, { uploadStatus: "blocked", failReason: "remote-mismatch" });
        return;
      }

      const blob = await this.blobs.get(page.blobKey);
      // The photo itself is gone from device storage — evicted under
      // storage pressure, or lost with cleared site data. There is nothing
      // left to upload, so retrying (which is what throwing here used to
      // do) buys a page that reads "Retrying" until the exam is over.
      // Block it instead and let the script screen ask for a retake.
      if (!blob) {
        this.updatePage(page.id, { uploadStatus: "blocked", failReason: "missing-blob" });
        return;
      }
      const uploaded = await this.store.uploadPage(folderId, name, blob, page.sha256, {
        replace: remote !== null,
      });

      if (uploaded.sha256 !== page.sha256) {
        this.handleChecksumMismatch(page);
        return;
      }

      this.markUploaded(page.id, uploaded.id);
    } catch (e) {
      if (e instanceof StoreAuthException) return this.globalPause(page, "auth");
      if (e instanceof StoreUnreachableException) return this.globalPause(page, "unreachable");
      if (e instanceof ConnectivityException) return this.globalPause(page, "offline");
      if (e instanceof StoreConflictException) {
        this.updatePage(page.id, { uploadStatus: "blocked", failReason: "remote-mismatch" });
        return;
      }
      this.scheduleRetry(page, String(e));
    } finally {
      this.inFlight.delete(page.id);
      // Refill from the collection as soon as a slot frees up, instead of
      // waiting for the next 5s poll tick — keeps the queuer saturated.
      void this.runOnce();
    }
  }

  private resolveFolder(scriptId: string, remoteId: string | null): Promise<string> {
    if (remoteId) return Promise.resolve(remoteId);
    let pending = this.folderResolution.get(scriptId);
    if (!pending) {
      pending = (async () => {
        const script = scriptsCollection.get(scriptId);
        if (!script) throw new Error(`missing script ${scriptId}`);
        const id = await this.store.ensureScript(scriptId, this.scannerName, scriptMetadataPayload(script));
        scriptsCollection.update(scriptId, draft => {
          draft.remoteId = id;
        });
        return id;
      })();
      this.folderResolution.set(scriptId, pending);
      // Never cache a failed resolution. The first upload of a script the
      // server hasn't seen goes through ensureScript, so an outage rejects
      // this promise — and a cached rejection replays that outage on every
      // later attempt, including the scanner's manual "Upload now", long
      // after the server is back. Dropping it makes the next attempt call
      // ensureScript again.
      pending.catch(() => {
        if (this.folderResolution.get(scriptId) === pending) this.folderResolution.delete(scriptId);
      });
    }
    return pending;
  }

  private handleChecksumMismatch(page: Page): void {
    if (page.failReason === "checksum-mismatch") {
      this.updatePage(page.id, { uploadStatus: "blocked", failReason: "checksum-mismatch" });
    } else {
      this.scheduleRetry(page, "checksum-mismatch");
    }
  }

  private markUploaded(id: string, driveFileId: string): void {
    this.updatePage(id, {
      uploadStatus: "uploaded",
      driveFileId,
      uploadedAt: this.now(),
      failReason: null,
      nextAttemptAt: null,
      // The retake has landed; a later re-upload of this same row (e.g. a
      // stuck-page reclaim) is an ordinary upload again.
      replaceRemote: false,
    });
  }

  private scheduleRetry(page: Page, failReason: string): void {
    const delayMs = backoffMs(page.attemptCount, this.random);
    this.updatePage(page.id, {
      uploadStatus: "failed",
      failReason,
      nextAttemptAt: this.now() + delayMs,
    });
  }

  /** Pauses without a page in hand — for callers outside the upload path
   * (the review-state poll gets the same 401 and should stop the queue
   * from hammering the server too). Routed through here rather than
   * `stateStore.setState` so an `auth` pause is timestamped and survives
   * the sweep's probe window. */
  pause(reason: PauseReason): void {
    if (reason === "auth") this.authPausedAt = this.now();
    this.stateStore.setState(() => ({ paused: true, pauseReason: reason }));
  }

  /** An outage should cost no backoff budget: undo the claim's attempt
   * increment and return the page to pending. */
  private globalPause(page: Page, reason: PauseReason): void {
    this.pause(reason);
    this.updatePage(page.id, {
      uploadStatus: "pending",
      attemptCount: page.attemptCount - 1,
    });
  }

  private updatePage(id: string, patch: Partial<Page>): void {
    trackPageMutation(
      id,
      pagesCollection.update(id, draft => {
        Object.assign(draft, patch);
      }),
    );
  }
}
